// The gallery. A list of filenames is the wrong way to read a folder of photographs, and most of what
// this module will hold is photographs — the residential corpus alone is 23,468 of them against 2,385
// PDFs. So: tiles, the thumbnail doing the work the name cannot.
//
// Thumbnails arrive as minted urls that expire with everything else the module hands out. A page left
// open outlives them, so a tile whose image fails asks the page for a fresh listing — once, because a
// blob that is genuinely gone must not put the page in a loop.

import React from 'react';
import { strings } from '/imports/strings';
import { formatBytes } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { DriveFileRow, DriveFolderRow, DriveSearchHit } from '/imports/api/drive';
import { DriveTarget, driveFileIcon } from '/imports/ui/DriveShared';

export interface DriveGridProps {
  folders: DriveFolderRow[];
  files: (DriveFileRow & Partial<DriveSearchHit>)[];
  inTrash: boolean;
  onOpenFolder(folderId: string): void;
  onOpenFile(fileId: string): void;
  onMenu(target: DriveTarget, el: HTMLElement): void;
  onStar(row: DriveFileRow): void;
  onThumbExpired(): void;
}

export const DriveGrid = (props: DriveGridProps) => (
  <div className="drive-grid">
    {props.folders.map((row) => (
      <div
        key={row._id}
        className="tile folder"
        role="button"
        tabIndex={0}
        onClick={() => (props.inTrash ? undefined : props.onOpenFolder(row._id))}
        onKeyDown={(e) => { if (e.key === 'Enter' && !props.inTrash) props.onOpenFolder(row._id); }}
      >
        <div className="art">
          <Icon icon="folder" size={56} fill={css.primaryColor} />
          {!!row.files && <span className="count">{row.files}</span>}
        </div>
        <div className="foot">
          <span className="label">{row.name}</span>
          <span className="tools">
            {!row.inherited && !!row.parentId && <Icon icon="lock" size={12} fill={css.grey4} />}
            <button
              type="button"
              className="icon-btn"
              aria-label={strings.Options()}
              onClick={(e) => { e.stopPropagation(); props.onMenu({ kind: 'folder', row }, e.currentTarget); }}
            >
              <Icon icon="tune" size={15} fill={css.grey3} />
            </button>
          </span>
        </div>
      </div>
    ))}

    {props.files.map((row) => (
      <div
        key={row._id}
        className="tile"
        role="button"
        tabIndex={0}
        onClick={() => props.onOpenFile(row._id)}
        onKeyDown={(e) => { if (e.key === 'Enter') props.onOpenFile(row._id); }}
      >
        <div className="art">
          {row.thumbUrl
            ? <img src={row.thumbUrl} alt="" loading="lazy" onError={() => props.onThumbExpired()} />
            : <Icon icon={driveFileIcon(row.ext)} size={48} fill={css.grey5} />}
          <button
            type="button"
            className={`icon-btn star ${row.starred ? 'on' : ''}`}
            aria-label={strings.Starred()}
            onClick={(e) => { e.stopPropagation(); props.onStar(row); }}
          >
            <Icon icon={row.starred ? 'starFilled' : 'star'} size={15} fill={row.starred ? '#e3a008' : css.grey5} />
          </button>
        </div>
        <div className="foot">
          <span className="label">{row.name}</span>
          <span className="tools">
            <button
              type="button"
              className="icon-btn"
              aria-label={strings.Options()}
              onClick={(e) => { e.stopPropagation(); props.onMenu({ kind: 'file', row }, e.currentTarget); }}
            >
              <Icon icon="tune" size={15} fill={css.grey3} />
            </button>
          </span>
        </div>
        <div className="sub muted">{row.path || formatBytes(row.size || 0)}</div>
      </div>
    ))}
  </div>
);
